import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import MaxWidthWrapper from '../../components/max-width-wrapper/MaxWidthWrapper';
import Loading from '../../components/Loading';
import { canUserEditProject } from '../../user/user-selectors';
import GeneralError from '../../components/GeneralError';
import styles from './project-sharing.module.less';
import queryString from 'query-string';
import { fetchSharingProgressForProject, setSharingProgressStepAsCompleted } from './project-sharing-progress-actions';
import {
    getCompletedSharingStepsForProject,
    getNumMissingSharingStepsForProject,
} from './project-sharing-progress-reducer';
import { trackPageView } from '../../support-and-tracking/analytics-actions';
import { ProjectSharingFacebook } from './ProjectSharingFacebook';
import { ProjectSharingFriends } from './ProjectSharingFriends';
import { ProjectSharingCommunity } from './ProjectSharingCommunity';
import { ProjectSharingSummaryPanel } from './ProjectSharingSummary';
import Main from '../../frame/main/Main';
import ProjectSharingCustomUrl from './ProjectSharingCustomUrl';
import { getLinkForProject } from '../../shortlink/shortlink-reducer';
import ProjectSharingInstagram from './ProjectSharingInstagram';
import { getProjectFromId } from '../projects-reducer';
import withRouter from '../../components/hooks/withRouter';
import { fetchProjectCard, fetchProjectTransactionAggregates } from '../project-actions';
import { fetchShortlinkForProject } from '../../shortlink/shortlink-actions';

export const sharingSteps = {
    customUrl: 'customUrl',
    facebook: 'facebook',
    friends: 'friends',
    community: 'community',
    instagram: 'instagram',
};

function ProjectSharingContent(props) {
    if (props.fromPublishedDialog) {
        return (
            <ProjectSharingCustomUrl
                {...props}
                completed={props.sharingProgress[sharingSteps.customUrl]}
                showHeaderAndFooter={false}
            />
        );
    }
    switch (props.step) {
        case '1':
            return <ProjectSharingCustomUrl {...props} completed={props.sharingProgress[sharingSteps.customUrl]} />;
        case '2':
            return <ProjectSharingFacebook {...props} completed={props.sharingProgress[sharingSteps.facebook]} />;
        case '3':
            return <ProjectSharingFriends {...props} completed={props.sharingProgress[sharingSteps.friends]} />;
        case '4':
            return <ProjectSharingCommunity {...props} completed={props.sharingProgress[sharingSteps.community]} />;
        case '5':
            return <ProjectSharingInstagram {...props} completed={props.sharingProgress[sharingSteps.instagram]} />;
        default:
            return <ProjectSharingSummaryPanel {...props} />;
    }
}

function ProjectSharing(props) {
    const { dispatch, params, project, user, sharingProgress, stepsMissing } = props;

    useEffect(() => {
        const url = `project-sharing${params.id ? `-${params.id}` : ''}`;
        trackPageView({ urlAsEvent: url, properties: { 'project-id': params.projectId } });
    }, [params.id, params.projectId]);

    const setSharingStepComplete = (step) => {
        const projectId = params.projectId;
        dispatch(setSharingProgressStepAsCompleted({ projectId, step }));
    };

    if (!project) {
        return (
            <Main>
                <Loading />
            </Main>
        );
    } else if (!canUserEditProject(user, project.id)) {
        return (
            <Main>
                <GeneralError error="Denne siden er bare for innsamler, du har ikke tilgang" />
            </Main>
        );
    }

    if (!project.published_at) {
        return (
            <Main>
                <MaxWidthWrapper className={styles.contentContainerNotPublished}>
                    <h1 className={styles.title}>Du må publisere innsamlingen før du deler den</h1>
                </MaxWidthWrapper>
            </Main>
        );
    }

    return (
        <Main>
            <MaxWidthWrapper className={styles.contentContainer}>
                <ProjectSharingContent
                    step={params.id}
                    sharingProgress={sharingProgress}
                    project={project}
                    stepsMissing={stepsMissing}
                    setSharingStepComplete={setSharingStepComplete}
                    fromPublishedDialog={props.fromPublishedDialog}
                    shortlink={props.shortlink}
                    showStreamLabs={props.showStreamLabs}
                />
            </MaxWidthWrapper>
        </Main>
    );
}

export const loader = async ({ dispatch, params }) => {
    const projectId = params.projectId;
    const fetchProjectPromise = dispatch(fetchProjectCard(projectId));
    const fetchProjectTransactionAggregatesPromise = dispatch(fetchProjectTransactionAggregates(projectId));
    const fetchSharingProgressForProjectPromise = dispatch(fetchSharingProgressForProject(projectId));
    const fetchShortlinkForProjectPromise = dispatch(fetchShortlinkForProject(projectId));

    await Promise.all([
        fetchProjectPromise,
        fetchProjectTransactionAggregatesPromise,
        fetchSharingProgressForProjectPromise,
        fetchShortlinkForProjectPromise,
    ]);
};

ProjectSharing.propTypes = {
    dispatch: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    project: PropTypes.object,
    fromPublishedDialog: PropTypes.bool,
};

ProjectSharing.defaultProps = { fromPublishedDialog: false };

function mapStateToProps(state, props) {
    const projectId = parseInt(props.params.projectId, 10);
    const project = getProjectFromId(state, projectId);
    const query = queryString.parse(props.location.search);
    const sharingProgress = getCompletedSharingStepsForProject(state, projectId);

    return {
        user: state.user,
        project,
        sharingProgress,
        isFetchingProjects: state.projects.isFetchingCards,
        stepsMissing: getNumMissingSharingStepsForProject(state, projectId),
        fromPublishedDialog: query.published === 'true',
        shortlink: getLinkForProject({ state, projectId }),
    };
}

export default withRouter(connect(mapStateToProps)(ProjectSharing));
